import { z } from 'zod'

import { pinResultSchema } from '@/platform/family/api'
import { type AppSupabaseClient } from '@/platform/supabase/client'
import { callRpc } from '@/platform/supabase/rpc'

/**
 * Learner PIN and learner session API (D-16), implemented by
 * `supabase/migrations/20261103000001_learner_pin_and_session.sql`.
 *
 * A learner session is started with the learner's own PIN inside the parent's
 * signed-in session and identified by an opaque token returned once. The
 * server derives the learner from the token; a learner id sent here only says
 * which profile the PIN is for. Tokens and PINs are never logged.
 */

export const entryLearnerSchema = z.object({
  learner_id: z.uuid(),
  nickname: z.string(),
  pin_configured: z.boolean(),
})
export type EntryLearner = z.infer<typeof entryLearnerSchema>

export const startResultSchema = z.discriminatedUnion('status', [
  z.object({
    status: z.literal('ok'),
    session_token: z.string().regex(/^[0-9a-f]{64}$/),
    learner_id: z.uuid(),
    nickname: z.string(),
    expires_at: z.string(),
    idle_timeout_seconds: z.number().int().positive(),
  }),
  z.object({ status: z.literal('pin_not_configured') }),
  z.object({ status: z.literal('wrong_pin'), attempts_remaining: z.number().int() }),
  z.object({ status: z.literal('locked'), locked_until: z.string() }),
])
export type StartResult = z.infer<typeof startResultSchema>

export const pingResultSchema = z.discriminatedUnion('valid', [
  z.object({
    valid: z.literal(true),
    learner_id: z.uuid(),
    nickname: z.string(),
    expires_at: z.string(),
  }),
  z.object({ valid: z.literal(false) }),
])
export type PingResult = z.infer<typeof pingResultSchema>

export const setLearnerPinResultSchema = pinResultSchema.extend({
  configured: z.boolean(),
})
export type SetLearnerPinResult = z.infer<typeof setLearnerPinResultSchema>

const endedSchema = z.object({ ended: z.number().int() })

export interface LearnerSessionApi {
  /** The family's learners for the selection screen, with PIN status. */
  listEntryLearners(): Promise<EntryLearner[]>
  /** Parent sets or resets a learner PIN; needs the parent PIN (step-up). */
  setLearnerPin(
    learnerId: string,
    newPin: string,
    parentPin: string,
  ): Promise<SetLearnerPinResult>
  start(learnerId: string, pin: string): Promise<StartResult>
  /** `active`: the learner did something since the last ping (extends idle time). */
  ping(token: string, active: boolean): Promise<PingResult>
  end(token: string, reason: 'ended' | 'switched'): Promise<void>
  /** Parent sign-out: ends every learner session of this parent login. */
  endAllMine(): Promise<void>
}

export function createLearnerSessionApi(client: AppSupabaseClient): LearnerSessionApi {
  return {
    listEntryLearners: () =>
      callRpc(client, 'list_entry_learners', {}, z.array(entryLearnerSchema)),
    setLearnerPin: (learnerId, newPin, parentPin) =>
      callRpc(
        client,
        'set_learner_pin',
        { p_learner_id: learnerId, p_new_pin: newPin, p_parent_pin: parentPin },
        setLearnerPinResultSchema,
      ),
    start: (learnerId, pin) =>
      callRpc(
        client,
        'start_learner_session',
        { p_learner_id: learnerId, p_pin: pin },
        startResultSchema,
      ),
    ping: (token, active) =>
      callRpc(
        client,
        'ping_learner_session',
        { p_session_token: token, p_active: active },
        pingResultSchema,
      ),
    end: async (token, reason) => {
      await callRpc(
        client,
        'end_learner_session',
        { p_session_token: token, p_reason: reason },
        endedSchema,
      )
    },
    endAllMine: async () => {
      await callRpc(client, 'end_my_learner_sessions', {}, endedSchema)
    },
  }
}
